import React from "react";
//#region types
import { GraphConstants } from "utils/constants";
import { ChartKeyTypes } from "models/propTypes/chartProps";
import { ChartEnums } from "models/enums";
import { DashboardMainChartProps } from "models/propTypes/homePropTypes";
//#endregion
//#region components
import { Chart, useChartContext } from "components/common";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import ReportWidgetType = WintSuperkollAPIModel.ReportWidgetType;

const DashboardMainChart: React.FC<DashboardMainChartProps> = (props) => {
  const chartApiKeys = new Map([
    [
      ChartEnums.MainDashBoardChartTypes.Sales,
      new ChartKeyTypes(
        GraphConstants.ChartKeys.DashboardSalesComparableBarWidget,
        GraphConstants.ChartKeys.DashboardSalesAccLineWidget
      ),
    ],
    [
      ChartEnums.MainDashBoardChartTypes.Cost,
      new ChartKeyTypes(
        GraphConstants.ChartKeys.DashboardCostsComparableWidget,
        GraphConstants.ChartKeys.DashboardCostsAccLineWidget
      ),
    ],
    [
      ChartEnums.MainDashBoardChartTypes.GrossMargin,
      new ChartKeyTypes(
        GraphConstants.ChartKeys.DashboardGrossMarginComparableWidget,
        GraphConstants.ChartKeys.DashboardGrossMarginAccLineWidget
      ),
    ],
    [
      ChartEnums.MainDashBoardChartTypes.Result,
      new ChartKeyTypes(
        GraphConstants.ChartKeys.DashboardResultComparableWidget,
        GraphConstants.ChartKeys.DashboardResultAccLineWidget
      ),
    ],
  ]);

  const [currentChartData, setCurrentChartData] = React.useState<{
    chartKey?: string;
    filter: object;
    cacheKey?: string;
  }>({
    filter: { type: ChartEnums.ChartDataFrequency.Monthly },
  });

  const chartContext = useChartContext();

  React.useEffect(() => {
    if (!chartContext?.filter) return;

    const selectedType =
      chartContext.filter.chartType !== undefined ? chartApiKeys.get(chartContext.filter.chartType) : undefined;
    if (!selectedType) return;

    const nextChartKey =
      chartContext.type === ChartEnums.DisplayType.Periodically ? selectedType.normal : selectedType.accumulated;
    const nextFilter = { type: chartContext.filter.frequency ?? ChartEnums.ChartDataFrequency.Monthly };
    const nextCacheKey = `${nextChartKey}-type-${nextFilter.type}`;

    setCurrentChartData((prev) => {
      const isSameChartKey = prev.chartKey === nextChartKey;
      const isSameFilterType = (prev.filter as any)?.type === nextFilter.type;
      const isSameCacheKey = prev.cacheKey === nextCacheKey;
      if (isSameChartKey && isSameFilterType && isSameCacheKey) return prev;
      return { chartKey: nextChartKey, filter: nextFilter, cacheKey: nextCacheKey };
    });
  }, [chartContext?.filter?.chartType, chartContext?.filter?.frequency, chartContext?.type]);

  const labelFormatter = (label?: string) =>
    label
      ? chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Daily
        ? label.slice(-2).replace("-", "0")
        : chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Quarterly ||
            chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Yearly
          ? label
          : label.substring(0, 3)
      : "";

  const toolTipFormatter = (label?: string) => {
    return label
      ? chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Daily
        ? label.slice(-2).replace("-", "0")
        : label
      : "";
  };

  const legendFormatter = (index: number, name?: string, startDate?: Date, endDate?: Date) =>
    (chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Monthly && startDate && endDate && startDate
      ? DateHelper.formatMonthRange(startDate, DateHelper.addDays(endDate, -1))
      : name) ?? "";

  const chart = React.useMemo(
    () =>
      currentChartData.chartKey !== undefined ? (
        <Chart
          useSkeleton={ReportWidgetType.Bar}
          id={"dashboard_main_chart"}
          chartAPIKey={currentChartData.chartKey}
          chartCacheKey={currentChartData.cacheKey}
          filter={currentChartData.filter}
          labelFormatter={labelFormatter}
          toolTipFormatter={toolTipFormatter}
          dataUnit={LocalizationHelperInstance.getFormattedMessage(GraphConstants.DefaultDataUnit)}
          onChartSuccessHandler={props.onChartSuccessHandler}
          hideLegend={
            chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Quarterly ||
            chartContext?.filter.frequency === ChartEnums.ChartDataFrequency.Yearly
          }
          legendPortalId={GraphConstants.DashboardMainChartContainerId}
          yAxisTickUnit={ChartEnums.YAxisTickUnitType.Currency}
          legendFormatter={legendFormatter}
        />
      ) : (
        <></>
      ),
    [currentChartData]
  );

  return chart;
};

export default DashboardMainChart;
